import React, { useEffect, useState } from 'react';
import { Accordion, Button } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import ContextAwareToggle from '../../../Profile/ContextAwareToggle';
import { sanitizeHtml, isHtmlContent } from '../../../Comments/utils/sanitizeHtml';
import RichTextEditor from '../../../Comments/RichTextEditor';
import { isReadonlyRole, isInternal } from '../../../../redux/actions/userAction';
import './CommunicationFeed.css';

const renderMessageContent = (message) => {
  if (isHtmlContent(message)) {
    return (
      <div
        className="communication-message communication-html"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(message) }}
      />
    );
  }
  return <div className="communication-message">{message}</div>;
};

const CommunicationFeed = ({ requestId }) => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [plainTextLength, setPlainTextLength] = useState(0);

  // Get current user LDAP ID
  let currentUserLdapId = '';
  try {
    const passData = JSON.parse(sessionStorage.getItem('passData'));
    currentUserLdapId = passData?.user || '';
  } catch (e) {
    /* ignore parse errors */
  }

  const fetchCommunications = () => {
    setLoading(true);
    setError(null);
    axios
      .get(
        global.REACT_APP_API_ENDPOINT +
          `/lender/request/communication/all?requestMasterTrackerId=${requestId}`
      )
      .then((response) => {
        const data = response.data || [];

        // Sort by createdDate descending
        const sorted = data.sort(
          (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
        );

        setCommunications(sorted);
      })
      .catch((err) => {
        setError(err);
        console.error('communication_feed_error', {
          requestMasterTrackerId: requestId,
          error: err.message,
          userId: currentUserLdapId,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (requestId) {
      fetchCommunications();
    }
  }, [requestId]);

  const handleSubmit = () => {
    setSubmitting(true);
    axios
      .post(
        global.REACT_APP_API_ENDPOINT +
          `/lender/request/communication?requestMasterTrackerId=${requestId}`,
        { message: htmlContent, isExternalUser: !isInternal(), createdBy: currentUserLdapId }
      )
      .then(() => {
        console.log('communication_submitted', {
          requestMasterTrackerId: requestId,
          messageLength: plainTextLength,
          userId: currentUserLdapId,
        });
        setShowEditor(false);
        setHtmlContent('');
        setPlainTextLength(0);
        fetchCommunications();
      })
      .catch((err) => {
        setError({ type: 'submit', message: err.message });
        console.error('communication_submit_error', {
          requestMasterTrackerId: requestId,
          error: err.message,
          userId: currentUserLdapId,
        });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    setShowEditor(false);
    setHtmlContent('');
    setPlainTextLength(0);
    setError(null);
  };

  const getDisplayName = (comm) => {
    if (!isInternal() && comm.isExternalUser === false) {
      return 'Fannie Mae';
    }

    return comm.createdByName || comm.createdByLdapId || '(n/a)';
  }

  const renderContent = () => {
    if (loading) {
      return <p className="communication-loading">Loading communications...</p>;
    }

    if (error && error.type !== 'submit') {
      return (
        <p className="communication-error">
          Unable to load communications.{' '}
          <button
            type="button"
            onClick={fetchCommunications}
            className="communication-retry"
          >
            Please try again.
          </button>
        </p>
      );
    }

    return (
      <div className="communication-content">
        {!isReadonlyRole() && !showEditor && (
          <div className="communication-toolbar">
            {communications.length === 0 && (
              <span className="communication-empty">No communications yet.</span>
            )}
            <Button
              className="btn-submit"
              onClick={() => { setShowEditor(true); setError(null); }}
            >
              Add Comment
            </Button>
          </div>
        )}

        {showEditor && (
          <div className="communication-editor-area">
            <RichTextEditor
              onChange={(html) => setHtmlContent(html)}
              onPlainTextChange={(text) => setPlainTextLength(text.length)}
              maxLength={5000}
              placeholder="Type your message..."
              ariaLabel="Communication message editor"
              error={false}
            />
            {error && error.type === 'submit' && (
              <p className="communication-error">
                Failed to send message. Please try again.
              </p>
            )}
            <div className="communication-actions">
              <Button
                className="btn-submit"
                onClick={handleSubmit}
                disabled={plainTextLength === 0 || submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </Button>
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {!showEditor && communications.length === 0 && isReadonlyRole() && (
          <p className="communication-empty">No communications yet.</p>
        )}

        {communications.length > 0 && (
          <div className="communication-list">
            {communications.map((comm, index) => (
              <div key={comm.requestCommunicationId || index} className={`communication-item${comm.isExternalUser === false ? ' internal' : ''}`}>
                <div className="communication-header">
                  <span className="communication-author">
                    {getDisplayName(comm)}
                  </span>
                  <span className="communication-time">
                    {moment(comm.createdDate).format('MM/DD/YY h:mm A')}
                  </span>
                </div>
                {renderMessageContent(comm.message)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="request-stack">
      <Accordion defaultActiveKey="0">
        <ContextAwareToggle eventKey="1">
          <p className="request-stack-header accordion-header-title">
            Communications
          </p>
        </ContextAwareToggle>
        <Accordion.Collapse eventKey="1">
          <div>{renderContent()}</div>
        </Accordion.Collapse>
      </Accordion>
    </div>
  );
};

export default CommunicationFeed;
